import React, { useRef, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { resolveMediaUrl } from "../lib/fileUrl";
import { Upload, X, Image as ImageIcon, Loader } from "lucide-react";
import { toast } from "sonner";
import MobileUploadSizeHint from "./MobileUploadSizeHint";
import { compressImageForUpload } from "../lib/compressImage";

/**
 * Image uploader — drag-drop + click. Stores canonical `/api/files/{id}` refs.
 * value is array of paths; onChange replaces the whole array.
 */
export default function ImageUploader({ value = [], onChange, max = 4, testid = "uploader" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const upload = async (files) => {
    if (!files || !files.length) return;
    if (value.length + files.length > max) {
      toast.error(`Max ${max} images`);
      return;
    }
    setBusy(true);
    try {
      const next = [...value];
      for (const file of files) {
        let compressed;
        try {
          compressed = await compressImageForUpload(file);
        } catch (e) {
          toast.error(e.message || "Could not compress image");
          continue;
        }
        const fd = new FormData();
        fd.append("file", compressed, compressed.name);
        const res = await axios.post(`${API}/upload`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        // Store relative path so URLs survive deploys / domain changes
        next.push(res.data.url);
      }
      onChange(next);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (i) => {
    const next = [...value];
    next.splice(i, 1);
    onChange(next);
  };

  return (
    <div data-testid={testid}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault(); setDrag(false);
          upload(Array.from(e.dataTransfer.files || []).filter(f => f.type.startsWith("image/")));
        }}
        onClick={() => inputRef.current?.click()}
        className={"zc-card p-6 border-2 border-dashed cursor-pointer transition " +
          (drag ? "border-[var(--zc-accent)] bg-[var(--zc-accent-soft)]" : "border-[var(--zc-border-2)] hover:border-[var(--zc-accent)]")}
        data-testid={testid + "-drop"}
      >
        <div className="flex flex-col items-center justify-center text-center py-3">
          {busy ? (
            <Loader size={22} className="text-[var(--zc-accent)] animate-spin" />
          ) : (
            <Upload size={22} className="text-[var(--zc-text-3)]" />
          )}
          <div className="text-sm font-semibold mt-2">{busy ? "Uploading…" : "Drop images here or click to upload"}</div>
          <div className="text-xs text-[var(--zc-text-3)] mt-1">JPG, PNG, WEBP — up to {max} images, max 5MB each</div>
        </div>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
          onChange={e => upload(Array.from(e.target.files || []))} data-testid={testid + "-input"} />
      </div>
      <MobileUploadSizeHint className="mt-2" />

      {value.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {value.map((url, i) => (
            <div key={url + i} className="relative group">
              <div className="aspect-[4/3] overflow-hidden rounded-lg border border-[var(--zc-border)]">
                <img src={resolveMediaUrl(url)} alt="" className="w-full h-full object-cover" />
              </div>
              <button type="button" onClick={(e) => { e.stopPropagation(); remove(i); }}
                className="absolute top-1.5 right-1.5 w-7 h-7 grid place-items-center rounded-full bg-white/95 hover:bg-white shadow-md"
                data-testid={`${testid}-remove-${i}`}>
                <X size={13} />
              </button>
              {i === 0 && (
                <div className="absolute bottom-1.5 left-1.5 zc-badge px-2 py-0.5 bg-[var(--zc-accent)] text-white">
                  Cover
                </div>
              )}
            </div>
          ))}
          {value.length < max && (
            <button type="button" onClick={() => inputRef.current?.click()}
              className="aspect-[4/3] grid place-items-center border-2 border-dashed border-[var(--zc-border-2)] rounded-lg hover:border-[var(--zc-accent)] text-[var(--zc-text-3)]"
              data-testid={testid + "-add"}>
              <ImageIcon size={20} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
